import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import OutboundTicketGrid from 'components/generic/datagrid/OutboundTicketGrid/OutboundTicketGrid'
import { useGetOutboundTicketsQuery } from 'services/outboundTickets'
import { OUTBOUND_TICKET_STATUSES, PAGE_SIZE, SORTABLE_OBT_FIELDS } from 'utils/constants'
import { clearConsumerInformation } from 'state/outboundTicket'
import { clearSalesOrderId, clearSalesOrderFilters } from 'state/salesOrder'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useSelectFilter } from 'utils/custom-hooks/useSelectFilter'

const OutboundTickets = () => {
  const { allowedOptions, renderSelectFilter } = useSelectFilter({
    localStorageKey: 'obt-grid-filter',
    options: [
      OUTBOUND_TICKET_STATUSES.INCOMPLETE,
      OUTBOUND_TICKET_STATUSES.PAID,
      OUTBOUND_TICKET_STATUSES.SHIPPED,
      OUTBOUND_TICKET_STATUSES.VOID,
      OUTBOUND_TICKET_STATUSES.INVOICED,
    ],
    defaultOptions: [
      OUTBOUND_TICKET_STATUSES.INCOMPLETE.id,
      OUTBOUND_TICKET_STATUSES.PAID.id,
      OUTBOUND_TICKET_STATUSES.INVOICED.id,
    ],
    label: 'Status',
  })

  const [queryOptions, setQueryOptions] = useState({
    filter: {
      sort: {
        field: SORTABLE_OBT_FIELDS.TICKET_ID,
        ascending: false,
      },
      statuses: allowedOptions,
      search: '',
    },
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isFetching } = useGetOutboundTicketsQuery(
    {
      ...queryOptions,
      filter: JSON.stringify(queryOptions.filter),
    },
    { skip: !queryOptions.filter?.sort?.field },
  )
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(clearConsumerInformation())
    dispatch(clearSalesOrderId())
    dispatch(clearSalesOrderFilters())
  }, [])

  useEffect(() => {
    setQueryOptions(prevQuery => ({
      ...prevQuery,
      filter: {
        ...prevQuery.filter,
        statuses: allowedOptions,
      },
    }))
  }, [allowedOptions])

  useGlobalLoader(false)

  const transformedRows = (data?.Items || []).slice().map(ticket => ({
    ...ticket,
    ticketId: isNaN(Number(ticket.tag)) ? ticket.tag : Number(ticket.tag),
    ticketStatus: OUTBOUND_TICKET_STATUSES[ticket.status]?.name,
    ticketEffectiveDate: ticket.effectiveDate ? new Date(ticket.effectiveDate) : new Date(ticket.date),
    ticketShipDate: ticket.firstShipDate && new Date(ticket.firstShipDate),
    customerInfo: {
      name: ticket.customerDisplayName,
      address: ticket.shippingInfo?.address,
    },
    salesOrder: ticket.contractInfo?.salesOrder || '',
    custOrder: ticket.contractInfo?.customerPo || '',
    truckNumber: ticket.transportationInfo?.carrierNumber,
    trailerNumber: ticket.transportationInfo?.trailerNumber,
    container: ticket.transportationInfo?.container,
    materials: ticket.materialTypeCodes || [],
    materialTypeCommonNames: ticket.materialTypeCommonNames || [],
    netWeight: ticket.netWeight,
    grossWeight: ticket.grossWeight,
    tareWeight: ticket.tareWeight,
  }))

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <OutboundTicketGrid
        rows={transformedRows}
        loading={isFetching}
        allowedOptions={allowedOptions}
        renderSelectFilter={renderSelectFilter}
        selectFilterPath="ticketStatus"
        filter={queryOptions?.filter}
        setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
        rowCount={data?.totalResultsCount || 0}
        paginationMode="server"
        useBackendSortAndFilter
        onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        setQueryOptions={setQueryOptions}
        autoPageSize={false}
        pageSize={queryOptions.pageSize}
      />
    </Grid>
  )
}

export default OutboundTickets
